import { createContext, useContext, useState, useEffect } from 'react'
import { signInWithPopup, signInWithCustomToken } from 'firebase/auth'
import { auth, googleProvider } from '../config/firebase'
import api from '../config/api'
import { useBranding } from './useBranding'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [client, setClient] = useState(null)
  const [loading, setLoading] = useState(true)
  const { fetchBranding } = useBranding()

  useEffect(() => {
    // Check for existing session
    const token = localStorage.getItem('portal_token')
    const savedClient = localStorage.getItem('portal_client')

    if (token && savedClient) {
      try {
        setClient(JSON.parse(savedClient))
      } catch {
        localStorage.removeItem('portal_token')
        localStorage.removeItem('portal_client')
      }
    }
    setLoading(false)
  }, [])

  const login = async (email, password, tenantId) => {
    const response = await api.post('/login', { email, password, tenantId })
    const { token, firebaseToken, client: clientData } = response.data.data

    localStorage.setItem('portal_token', token)
    localStorage.setItem('portal_client', JSON.stringify(clientData))
    setClient(clientData)

    // Establish a Firebase Auth session so Firestore security rules (realtime messages,
    // typing indicators) have a request.auth context to check — the portal JWT above is a
    // separate, non-Firebase mechanism and doesn't set request.auth on its own.
    if (firebaseToken) {
      try {
        await signInWithCustomToken(auth, firebaseToken)
      } catch (err) {
        console.warn('Failed to establish Firebase session for realtime features:', err.message)
      }
    }

    // Fetch tenant branding after login
    if (clientData.tenantId) {
      fetchBranding(clientData.tenantId)
    }

    return clientData
  }

  const googleLogin = async () => {
    // Use Firebase Auth for Google Sign-In
    const result = await signInWithPopup(auth, googleProvider)
    const idToken = await result.user.getIdToken()

    // Send the Firebase ID token to our backend
    const response = await api.post('/auth/google', {
      credential: idToken,
      firebaseUid: result.user.uid,
      email: result.user.email,
      displayName: result.user.displayName,
      photoURL: result.user.photoURL
    })

    const { token, firebaseToken, client: clientData } = response.data.data

    localStorage.setItem('portal_token', token)
    localStorage.setItem('portal_client', JSON.stringify(clientData))
    setClient(clientData)

    // Swap from the personal Google-linked Firebase session to the portal-scoped one so
    // Firestore rules see the portalClientId/tenantId claims they expect (same as email/password
    // login) — nothing downstream needs the original Google session after this point.
    if (firebaseToken) {
      try {
        await signInWithCustomToken(auth, firebaseToken)
      } catch (err) {
        console.warn('Failed to establish Firebase session for realtime features:', err.message)
      }
    }

    // Fetch tenant branding after Google login
    if (clientData.tenantId) {
      fetchBranding(clientData.tenantId)
    }

    return clientData
  }

  const logout = () => {
    localStorage.removeItem('portal_token')
    localStorage.removeItem('portal_client')
    setClient(null)
    // Also sign out from Firebase
    auth.signOut().catch(() => {})
  }

  const requestPasswordReset = async (email, tenantId) => {
    const response = await api.post('/password-reset/request', { email, tenantId })
    return response.data
  }

  const resetPassword = async (token, newPassword) => {
    const response = await api.post('/password-reset', { token, newPassword })
    return response.data
  }

  const value = {
    client,
    loading,
    isAuthenticated: !!client,
    login,
    googleLogin,
    logout,
    requestPasswordReset,
    resetPassword
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default useAuth
